import myAxios from "./axiositeachWrapper";
import Config from "../_config/config";

// ========================================
// Types
// ========================================

export interface SearchedQuestion {
  id: number;
  source: "question_bank" | "test_section";
  name: string;
  stemStatement: string;
  mark: number;
  questionCategory?: number;
  tags?: string;
  topicName?: string;
  difficultyName?: string;
  questionTypeName?: string;
  options?: string[];
  concept?: string;
  correctAnswer?: string;
}

export interface InlineQuestionOption {
  text: string;
  isCorrect: boolean;
}

export interface InlineQuestionPayload {
  questionText: string;
  questionType: "mcq" | "tf" | "descriptive";
  options: InlineQuestionOption[];
  marks: number;
  difficulty: string;
  concept: string;
}

export interface SectionInput {
  name: string;
  questionIds: number[];
  source: "question_bank" | "test_section";
  keywordId?: number;
  inlineQuestions?: InlineQuestionPayload[];
  subSections?: SectionInput[];
}

export interface ValidationResult {
  success: boolean;
  valid: boolean;
  errors: string[];
  questions?: Array<{
    id: number;
    source: string;
    name: string;
    stemStatement: string;
    mark: number;
  }>;
  inlineQuestionCount?: number;
}

export interface CreateQuizPayload {
  name: string;
  sections: SectionInput[];
  categoryId?: number;
  testTypeId?: number;
  playerId?: number;
  testDate?: string;
  testTime?: number;
  testResultTypeId?: number;
  startDate?: string;
  endDate?: string;
  testCutOffTime?: string;
  sortOrder?: number;
  choiceSequence?: string;
  password?: string;
  remarks?: string;
  rankDefinition?: boolean;
  yearIds?: number[];
  /** Web instructions (HTML) — stored on the root section. */
  instructions?: string;
  /** Mobile instructions (HTML) — stored on the root section. */
  mobileInstructions?: string;
  /** Test results declaration message (HTML) — test_master.TestDeclarationRemarks. */
  testDeclarationRemarks?: string;
  /** Student announcement remarks (HTML) — test_master.StudentAnnouncementRemarks. */
  studentAnnouncementRemarks?: string;
}

export type UpdateQuizPayload = CreateQuizPayload;

export interface CreateQuizResult {
  testId: number;
  rootSectionId: number;
  questionSetId: number;
  name: string;
  totalMarks: number;
  totalQuestions: number;
  sectionsCount: number;
}

export interface QuizDetails {
  testId: number;
  name: string;
  displayName?: string;
  testDate?: string;
  testTime?: number;
  categoryId?: number;
  categoryName?: string;
  /** True when the test's category Config has needsManualEvaluation (DVS gate). */
  needsManualEvaluation?: boolean;
  /** test_master.TestEvaluationType id (DVS allocation allowed for null/1/3). */
  evaluationTypeId?: number | null;
  testTypeId?: number;
  testTypeName?: string;
  testTypeDisplayLabel?: string;
  /** test_type_master.OnlineFlag — 1 for online test types. Gates the
   *  "Make Solutions Public" toggle (only meaningful for online tests). */
  onlineFlag?: number;
  playerId?: number;
  playerName?: string;
  testResultTypeId?: number;
  rankDefinition?: boolean;
  startDate?: string;
  endDate?: string;
  testCutOffTime?: string;
  sortOrder?: number;
  choiceSequence?: string;
  remarks?: string;
  /** Web instructions (HTML) from the root section. */
  instructions?: string;
  /** Mobile instructions (HTML) from the root section. */
  mobileInstructions?: string;
  /** Test results declaration message (HTML) — test_master.TestDeclarationRemarks. */
  testDeclarationRemarks?: string;
  /** Student announcement remarks (HTML) — test_master.StudentAnnouncementRemarks. */
  studentAnnouncementRemarks?: string;
  testMasterPassword?: string;
  publishStatusId?: number;
  inputType?: number;
  testCode?: string;
  termId?: number;
  creditPoints?: number;
  keywordId?: number;
  keywordName?: string;
  keywordCode?: string;
  /** Test-level total marks stored on the root section row. */
  totalMarks?: number;
  auditJson?: string;
  allowRetake?: boolean;
  retakeAlreadyEnabled?: boolean;
  allowQuestionRules?: boolean;
  hasManualQuestions?: boolean;
  yearIds?: number[];
  sections: Array<{
    sectionId: number;
    name: string;
    totalMarks: number;
    totalQuestions: number;
    keywordId?: number;
    keywordName?: string;
    keywordCode?: string;
    isRankComputation?: boolean;
    passMark?: number;
    negativeMarks?: number;
    sectionalTestTime?: number;
    rankOrder?: number;
    testSectionDate?: string | null;
    examStartTime?: string | null;
    examEndTime?: string | null;
    sectionConfig?: string | null;
    questions: Array<{
      mappingId: number;
      questionId: number;
      questionName: string;
      stemStatement: string;
      marks: number;
      questionNumber: number;
      testQuestionNumber: number;
    }>;
    subSections: Array<{
      sectionId: number;
      name: string;
      totalMarks: number;
      totalQuestions: number;
      keywordId?: number;
      questions: any[];
    }>;
  }>;
  auditTrail?: Array<{
    id: number;
    action: string;
    actedOn: string;
    remarks?: string;
    userId?: number;
    userName?: string;
    remoteHost?: string;
    itemTypeName?: string;
  }>;
  testTags?: Array<{
    mappingId: number;
    testId: number;
    tagId: number;
    tagName?: string;
    tagType?: string;
  }>;
  schoolMappings?: Array<{
    mappingId: number;
    schoolId?: number;
    schoolName?: string;
    tagId?: number;
    tagName?: string;
    testDate?: string;
  }>;
  supplementaryIndexId?: number;
  supplementaryIndexName?: string;
  isBacklog?: boolean;
  courseMappings?: Array<{
    mappingId: number;
    courseId?: number;
    courseName?: string;
    batchId?: number;
    batchName?: string;
    publishStatusId?: number;
    programId?: number;
    programName?: string;
    termId?: number;
    termName?: string;
    termDisplayName?: string;
  }>;
}

export interface QuizListItem {
  testId: number;
  name: string;
  testDate?: string;
  /** Scheduled window start (test_master.StartDate). */
  startDate?: string;
  /** Scheduled window end (test_master.EndDate). */
  endDate?: string;
  testTime?: number;
  categoryId?: number;
  categoryName?: string;
  testTypeId?: number;
  testTypeName?: string;
  playerId?: number;
  playerName?: string;
  totalMarks?: number;
  totalQuestions?: number;
}

export interface ProgramTestItem {
  mappingId: number;
  testId: number;
  testName: string;
  testDate: string | null;
  testTime: number | null;
  totalMarks: number;
  totalQuestions: number;
  categoryId: number | null;
  categoryName: string | null;
  courseId: number | null;
  courseName: string | null;
  publishStatusId: number | null;
  publishStatus: string | null;
}

export interface ProgramTestsLookups {
  programs: Array<{ id: number; name: string }>;
  years: Array<{ id: number; name: string }>;
  categories: Array<{ id: number; name: string }>;
}

export interface LookupData {
  categories: Array<{ id: number; name: string }>;
  testTypes: Array<{ id: number; name: string; displayLabel: string }>;
  players: Array<{ id: number; name: string; displayName: string }>;
  keywords: Array<{ id: number; name: string; code: string }>;
  years: Array<{ id: number; name: string }>;
  supplementaryIndices?: Array<{ id: number; name: string }>;
  /** Centres (active school_master rows) for the Manage Tests "Centre" filter. */
  schools?: Array<{ id: number; name: string }>;
}

export interface MonitoringStudent {
  studentTestSectionId: number;
  enquiryId: number;
  enrollmentId: number;
  studentName: string;
  studentCode: string;
  batchId?: number;
  marks: number | null;
  totalMarks: number;
  status: number;
  submitStatus: number;
  statusLabel: string;
  entryDate: string | null;
  questionsAnswered: number;
  questionsTotal: number;
  markedForReview: number;
  lastActivity: string | null;
  attempts: number;
}

export interface MonitoringData {
  test: {
    testId: number;
    name: string;
    testDate: string;
    testTime: number;
    totalMarks: number;
    totalQuestions: number;
  };
  stats: {
    totalStudents: number;
    submitted: number;
    inProgress: number;
    notStarted: number;
    averageMarks: number;
    highestMarks: number;
    lowestMarks: number;
  };
  students: MonitoringStudent[];
}

export interface ResultStudent {
  enquiryId: number;
  enrollmentId: number;
  studentName: string;
  studentCode: string;
  batchId?: number;
  batchName?: string | null;
  batchCode?: string | null;
  username?: string | null;
  password?: string | null;
  year?: string | null;
  courseName?: string | null;
  totalMarks: number | null;
  maxMarks: number;
  percentage: number;
  rank: number | null;
  testRank?: number | null;
  schoolRank?: number | null;
  batchRank?: number | null;
  studentGrade?: string | null;
  status: string;
  testStatus?: "ABSENT" | "PRESENT" | "DNA" | null;
  entryDate: string | null;
  correct: number;
  wrong: number;
  unattempted: number;
  sectionMarks: Record<number, number | null>;
}

export interface ResultSection {
  sectionId: number;
  name: string;
  totalMarks: number;
  totalQuestions: number;
}

export interface TestResultsData {
  test: {
    testId: number;
    name: string;
    testDate: string;
    testTime: number;
    totalMarks: number;
    totalQuestions: number;
  };
  sections: ResultSection[];
  stats: {
    totalStudents: number;
    submitted: number;
    averageMarks: number;
    averagePercentage: number;
    highestMarks: number;
    lowestMarks: number;
    passCount: number;
    failCount: number;
  };
  students: ResultStudent[];
}

/** One webcam capture row in the proctoring metrics table. */
export interface ProctorCapture {
  sno: number;
  photoUrl: string;
  mobileCheck: string;
  similarity: string;
  faces: number;
  zeroFaces: number;
  mood: string;
  moodConfidenceFactor: number;
  createdOn: string;
}

/** Payload for the webcam proctoring screen (Node port of WebCamProctoring.jsp). */
export interface WebProctoringData {
  testId: number;
  enquiryId: number;
  userId: number | null;
  testName: string;
  testDate: string;
  customerName: string;
  tenantTestIdUserId: string;
  studentName: string;
  studentCode: string;
  profilePhotoUrl: string;
  metrics: string[];
  violationSummary: { authenticateFace: string; mobile: string; secondFace: string; zeroFace: string };
  captures: ProctorCapture[];
  blockTest: boolean;
  blanketFlag: string;
  /** Test Block control is enabled for this tenant (JsonConfig.test.webProctoring.enableStatus===1). */
  testBlockEnabled: boolean;
  testBlockPermission: boolean;
  blanketBlockPermission: boolean;
}

export interface AnswerKeyFile {
  id: number;
  testId?: number;
  fileName: string;
  size?: number;
  createdDate?: string;
  url: string | null;
}

// ========================================
// QuickQuizService
// ========================================

class QuickQuizService {
  private baseUrl = `${Config.nodeSiteUrl}v1/quick-quiz`;

  /**
   * Fetch lookup data for form dropdowns (categories, types, players, keywords)
   */
  async getLookups(): Promise<LookupData> {
    const response = await myAxios.get(`${this.baseUrl}/lookups`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data;
  }

  /**
   * Search questions from question bank and/or existing test questions
   */
  async searchQuestions(
    q: string,
    source: "question_bank" | "test_section" | "both" = "both",
    limit = 20,
    offset = 0
  ): Promise<{ questions: SearchedQuestion[]; total: number }> {
    const response = await myAxios.get(`${this.baseUrl}/search-questions`, {
      params: { q, source, limit, offset },
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data;
  }

  /**
   * Validate section structure and question IDs before creation
   */
  async validateSections(sections: SectionInput[]): Promise<ValidationResult> {
    const response = await myAxios.post(`${this.baseUrl}/validate`, {
      sections,
    });
    return response.data as any;
  }

  /**
   * Create a quick quiz from existing question IDs
   */
  async createQuiz(
    payload: CreateQuizPayload
  ): Promise<{ success: boolean; data: CreateQuizResult; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/create`, payload);
    return response.data as any;
  }

  /**
   * Update an existing test
   */
  async updateQuiz(
    testId: number,
    payload: UpdateQuizPayload
  ): Promise<{ success: boolean; data: CreateQuizResult; message: string }> {
    const response = await myAxios.put(`${this.baseUrl}/${testId}`, payload);
    return response.data as any;
  }

  /**
   * Get full quiz details by test ID
   */
  async getQuiz(testId: number): Promise<QuizDetails> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data;
  }

  /**
   * Upload an answer-key PDF for a test. Field name "file". PDF only.
   */
  async uploadAnswerKey(
    testId: number,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<{ success: boolean; message?: string; data?: AnswerKeyFile }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/answer-key`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e: ProgressEvent) =>
          e.total && onProgress?.(Math.round((e.loaded * 100) / e.total)),
      }
    );
    return response.data as any;
  }

  /**
   * List answer-key PDFs for a test (URLs are presigned).
   */
  async listAnswerKeys(testId: number): Promise<AnswerKeyFile[]> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/answer-keys`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((response.data as any)?.data as AnswerKeyFile[]) || [];
  }

  /**
   * Soft-delete an answer-key PDF.
   */
  async deleteAnswerKey(
    testId: number,
    keyId: number
  ): Promise<{ success: boolean; message?: string }> {
    const response = await myAxios.delete(
      `${this.baseUrl}/${testId}/answer-key/${keyId}`
    );
    return response.data as any;
  }

  /**
   * Trigger finalize & bundle for a test (proxied through nodefork to Java backend)
   */
  async finalizeAndBundle(
    testId: number,
    downloadBefore: number = 0
  ): Promise<{ success: boolean; status: number; message: string }> {
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/finalize-bundle`,
      { downloadBefore }
    );
    return response.data as any;
  }

  /**
   * Populate student_test_section from student_test_section_question_mapping
   */
  async populateTestSections(testId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/populate`);
    return response.data as any;
  }

  /**
   * Partial (non-destructive) populate — adds ONLY students present in DynamoDB
   * but missing from both stsqm and student_test_section. Existing records are
   * left untouched (nothing is deleted).
   */
  async populateMissingStudents(testId: number): Promise<{
    success: boolean;
    message: string;
    populated?: number;
    checked?: number;
    skippedExisting?: number;
    noDynamoData?: number;
    candidates?: number;
  }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/populate-missing`);
    return response.data as any;
  }

  /**
   * Repair students whose answer sheet was finalized entirely blank (zero marks)
   * while their real answers are still in DynamoDB. Re-reads DynamoDB (consistent)
   * per blank student and re-populates + grades those with answers; genuine
   * no-shows stay blank, and students whose DynamoDB read fails are skipped.
   */
  async repairBlankSheets(testId: number): Promise<{
    success: boolean;
    message: string;
    blankStudents?: number;
    repaired?: number;
    stillEmpty?: number;
    readFailed?: number;
    noUser?: number;
  }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/repair-blank-sheets`);
    return response.data as any;
  }

  /**
   * Batches (with active-student counts) mapped to the test — used to populate
   * the batch picker in the Mark Absentees modal.
   */
  async getMarkAbsenteeBatches(testId: number): Promise<{
    success: boolean;
    batches: Array<{ batchId: number; batchName: string; studentCount: number }>;
    message?: string;
  }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/mark-absentees/batches`);
    return response.data as any;
  }

  /**
   * Mark every eligible student (in the selected batches) who has not attempted
   * the test as Absent (auto-absent rows in student_test_section). Idempotent.
   * When `batchIds` is omitted/empty, all eligible students are considered.
   */
  async markAbsentees(testId: number, batchIds?: number[]): Promise<{
    success: boolean;
    message: string;
    marked?: number;
    eligibleStudents?: number;
    skipped?: number;
  }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/mark-absentees`, {
      batchIds: batchIds ?? [],
    });
    return response.data as any;
  }

  /**
   * Download the hall-ticket PDF (one page per mapped student) generated from
   * the tenant's template_master template.
   */
  /**
   * Fetch the hall-ticket HTML for preview (every student, ready to render
   * in an iframe via srcDoc). Does not run puppeteer — fast.
   */
  async previewHallTicket(testId: number, batchId?: number, withPhoto: boolean = true): Promise<{ html: string; studentCount: number }> {
    const body: Record<string, unknown> = { preview: true, withPhoto };
    if (batchId && batchId > 0) body["batchId"] = batchId;
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/hall-ticket`,
      body,
      { responseType: "text" },
    );
    const headerVal = (response.headers as any)?.["x-student-count"];
    const studentCount = headerVal ? Number(headerVal) || 0 : 0;
    return { html: String(response.data ?? ""), studentCount };
  }

  /**
   * Generate the hall-ticket PDF and trigger a browser download.
   * Uses a 6-minute axios timeout — the backend caps puppeteer at 5 minutes
   * for very large batches (up to 1500 students), so the client must wait at
   * least that long.
   */
  async generateHallTicket(testId: number, batchId?: number, withPhoto: boolean = true): Promise<void> {
    const body: Record<string, unknown> = { withPhoto };
    if (batchId && batchId > 0) body["batchId"] = batchId;
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/hall-ticket`,
      body,
      { responseType: "blob", timeout: 360000 },
    );
    // myAxios's response type is generic — at runtime `responseType: "blob"`
    // gives us a Blob; cast explicitly so TS accepts it as a BlobPart.
    const blob = new Blob([response.data as unknown as BlobPart], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${testId}${batchId ? `_batch${batchId}` : ""}_Admission_Ticket.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Fetch the Mark Foil HTML for preview (one foil per mapped course, students
   * chunked 40 per page). Skips puppeteer — fast.
   */
  async previewMarkFoil(testId: number): Promise<{ html: string; pageCount: number }> {
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/mark-foil`,
      { preview: true },
      { responseType: "text" },
    );
    const headerVal = (response.headers as any)?.["x-mark-foil-pages"];
    const pageCount = headerVal ? Number(headerVal) || 0 : 0;
    return { html: String(response.data ?? ""), pageCount };
  }

  /**
   * Generate the Mark Foil PDF and trigger a browser download.
   */
  async generateMarkFoil(testId: number): Promise<void> {
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/mark-foil`,
      {},
      { responseType: "blob", timeout: 240000 },
    );
    const blob = new Blob([response.data as unknown as BlobPart], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${testId}_MarkFoil.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Lightweight metadata: total students mapped + per-batch breakdown. Used
   * by the frontend to decide whether to show the batch picker before the
   * (expensive) hall-ticket render. No puppeteer, no photo fetching.
   */
  async listHallTicketBatches(testId: number): Promise<{
    studentCount: number;
    batches: Array<{ batchId: number; batchName: string; studentCount: number }>;
  }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/hall-ticket/batches`);
    const data = (response.data ?? {}) as {
      success?: boolean;
      studentCount?: number;
      batches?: Array<{ batchId: number; batchName: string; studentCount: number }>;
    };
    return {
      studentCount: Number(data.studentCount ?? 0),
      batches: Array.isArray(data.batches) ? data.batches : [],
    };
  }

  /**
   * Validate a batch of test IDs. Returns which exist (with name + category +
   * total marks + question count) and which are missing.
   */
  async lookupTests(testIds: number[]): Promise<{
    found: Array<{
      id: number;
      name: string;
      categoryName: string | null;
      totalMarks: number | null;
      totalQuestions: number | null;
    }>;
    missing: number[];
  }> {
    const response = await myAxios.post(`${this.baseUrl}/lookup-tests`, { testIds });
    return response.data as any;
  }

  /**
   * Merge multiple source tests into a single target test (creates a new
   * test_master when targetTestId is omitted). Copies sections,
   * student_test_section, course mappings, and school mappings.
   */
  async mergeSequenceTests(payload: {
    sourceTestIds: number[];
    testName?: string;
    targetTestId?: number;
    targetCategoryId?: number;
    copyStudentMarks?: boolean;
    copyCourseMappings?: boolean;
    copySchoolMappings?: boolean;
  }): Promise<{
    status: "success" | "error";
    message: string;
    targetTestId?: number;
    targetTestName?: string;
    createdSectionCount?: number;
    totalMarks?: number;
    isNewTest?: boolean;
    insertedStudentSections?: number;
    uniqueStudents?: number;
    uniqueSections?: number;
    insertedCourseMappings?: number;
    uniqueCourses?: number;
    uniqueBatches?: number;
    insertedSchoolMappings?: number;
    uniqueSchools?: number;
  }> {
    const response = await myAxios.post(`${this.baseUrl}/sequence-merge`, payload);
    return response.data as any;
  }

  /**
   * Get bundle generation status for a test
   */
  async getBundleStatus(
    testId: number
  ): Promise<{
    success: boolean;
    data: {
      status: string;
      progressPercentage: number;
      totalPapers: number;
      papersCompleted: number;
      startedAt?: string;
      completedAt?: string;
      errorMessage?: string;
      retryCount?: number;
    } | null;
  }> {
    const response = await myAxios.get(
      `${this.baseUrl}/${testId}/bundle-status`
    );
    return response.data as any;
  }

  /**
   * Get test results — all students' marks, section breakdown, ranks, stats
   */
  async getTestResults(testId: number): Promise<TestResultsData> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/results`);
    return (response.data as any)?.data;
  }

  /**
   * Get test monitoring data — students, status, marks, submission info
   */
  async getTestMonitoring(testId: number): Promise<MonitoringData> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/monitoring`);
    return (response.data as any)?.data;
  }

  /**
   * Update test metadata (name, dates, category, etc.) without touching sections/questions
   */
  async updateTestInfo(
    testId: number,
    payload: {
      name?: string;
      testDate?: string;
      testTime?: number;
      categoryId?: number;
      testTypeId?: number;
      playerId?: number;
      testResultTypeId?: number;
      startDate?: string;
      endDate?: string;
      testCutOffTime?: string;
      sortOrder?: number;
      choiceSequence?: string;
      remarks?: string;
      password?: string;
      rankDefinition?: boolean;
      keywordId?: number | null;
      supplementaryIndex?: number | null;
      isBacklog?: boolean;
      /** Test-level total marks. Backend rejects if < sum of section marks. */
      totalMarks?: number;
      yearIds?: number[];
      instructions?: string;
      mobileInstructions?: string;
      testDeclarationRemarks?: string;
      studentAnnouncementRemarks?: string;
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.patch(`${this.baseUrl}/${testId}/info`, payload);
    return response.data as any;
  }

  /**
   * Get marks moderation data — per-student, per-question marks
   */
  async getMarksModeration(testId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/marks-moderation`);
    return (response.data as any)?.data;
  }

  /**
   * Update section-wise marks, recalculate totals, ranks, and grades
   */
  async moderateMarks(
    testId: number,
    marks: Array<{ stsId: number; value: number; remarks?: string; modificationTypeId?: number }>
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.patch(`${this.baseUrl}/${testId}/moderate-marks`, { marks });
    return response.data as any;
  }

  /**
   * Compute ranks and grades for a test (without modifying marks).
   */
  async computeRanksAndGrades(testId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/compute-ranks-grades`);
    return response.data as any;
  }

  /**
   * Load all test questions with options, correct answers, marks.
   */
  async getTestQuestions(testId: number): Promise<{
    success: boolean;
    data: {
      test: { testId: number; name: string; totalQuestions: number };
      questions: Array<{
        mappingId: number;
        sectionId: number;
        sectionName: string;
        questionId: number;
        questionNumber: number;
        testQuestionNumber: number;
        questionName: string;
        stemStatement: string;
        questionCategory: number;
        marks: number;
        negativeMarks: number;
        /** -1 = DEFAULT, 1 = PARTIAL, 2 = ONE-OF (test_section_question_mapping.MarkingScheme) */
        markingScheme: number;
        bonusMark: boolean;
        /** Integer (cat 4) answer key. */
        answer: string;
        /** Integer Range (cat 5) bounds. */
        answerRangeStart: number | null;
        answerRangeEnd: number | null;
        choices: Array<{ choiceString: string; name: string; correctFlag: number }>;
      }>;
    };
  }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/test-questions`);
    return response.data as any;
  }

  /**
   * Update a question's marks, negative marks, marking scheme, bonus mark, and
   * correct choices. Recalculates student marks on the backend.
   */
  async updateTestQuestion(
    testId: number,
    mappingId: number,
    payload: {
      marks?: number;
      negativeMarks?: number;
      /** -1 = DEFAULT, 1 = PARTIAL, 2 = ONE-OF */
      markingScheme?: number;
      bonusMark?: boolean;
      correctChoices?: string[];
      /** Integer (cat 4) answer key. */
      answer?: string;
      /** Integer Range (cat 5) bounds. */
      answerRangeStart?: number | null;
      answerRangeEnd?: number | null;
    },
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.patch(`${this.baseUrl}/${testId}/test-questions/${mappingId}`, payload);
    return response.data as any;
  }

  /**
   * Clone (duplicate) a test — optionally without questions.
   */
  async cloneTest(
    testId: number,
    payload: {
      name?: string;
      testDate?: string;
      startDate?: string;
      endDate?: string;
      excludeQuestions?: boolean;
      excludeCourseMappings?: boolean;
    },
  ): Promise<{
    success: boolean;
    message: string;
    data?: { testId: number; name: string; totalMarks: number; totalQuestions: number };
  }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/clone`, payload);
    return response.data as any;
  }

  /**
   * Enable retake test (proxied through nodefork to Java backend)
   */
  async enableRetake(
    testId: number
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/enable-retake`);
    return response.data as any;
  }

  /**
   * Update section metadata (name, passMark, keyword, rank computation, time, etc.)
   */
  async updateSectionInfo(
    sectionId: number,
    payload: {
      name?: string;
      totalMarks?: number;
      passMark?: number;
      keywordId?: number | null;
      isRankComputation?: boolean;
      sectionalTestTime?: number | null;
      negativeMarks?: number | null;
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.patch(`${this.baseUrl}/section/${sectionId}/info`, payload);
    return response.data as any;
  }

  /**
   * Get alert templates for Send Alert modal
   */
  async getAlertTemplates(testId: number): Promise<{ templates: Array<{ id: number; name: string; body: string; channel: string }> }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/alert-templates`);
    return (response.data as any)?.data;
  }

  /**
   * Send alert to students/parents (proxied through nodefork to Java backend)
   */
  async sendAlert(
    testId: number,
    payload: {
      templateId?: number;
      alertVia: { sms: boolean; email: boolean; whatsapp: boolean };
      sendTo: { student: boolean; father: boolean; mother: boolean; guardian: boolean };
      filterByAttendance: { present: boolean; absent: boolean };
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/send-alert`, payload);
    return response.data as any;
  }

  /**
   * Get publish lookups (schools, courses, batches, programs, publishStatuses)
   */
  async getPublishLookups(testId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/publish-lookups`);
    return (response.data as any)?.data;
  }

  /** Issue to school or tag */
  async issueToSchool(testId: number, payload: { schoolId?: number; tagId?: number }): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/issue-school`, payload);
    return response.data as any;
  }

  /** Remove school/tag mapping */
  async removeSchoolMapping(testId: number, mappingId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/issue-school/${mappingId}`);
    return response.data as any;
  }

  /** Issue to course (single or multiple) */
  async issueToCourse(testId: number, payload: { courseId?: number; courseIds?: number[]; publishStatusId?: number; batchId?: number; programId?: number; termId?: number }): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/issue-course`, payload);
    return response.data as any;
  }

  /** Remove course mapping */
  async removeCourseMapping(testId: number, mappingId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/issue-course/${mappingId}`);
    return response.data as any;
  }

  /** Issue to program */
  async issueToProgram(testId: number, programId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/issue-program`, { programId });
    return response.data as any;
  }

  /** Get tag lookups (tag types + tags + mapped tags) */
  /** Get question sets for a test */
  async getTestSets(testId: number): Promise<{ sets: Array<{ id: number; name: string; consumed: boolean; json?: string }> }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/sets`);
    return (response.data as any)?.data;
  }

  /** Get set details with section-wise questions */
  async getSetQuestions(testId: number, setId: number): Promise<{
    set: { id: number; name: string; consumed: boolean };
    sections: Array<{ sectionId: number; sectionName: string; questions: Array<{ mappingId: number; questionId: number; questionName: string; stemStatement: string; setOrder: number }> }>;
  }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/sets/${setId}`);
    return (response.data as any)?.data;
  }

  /** Create a new question set */
  async createSet(testId: number, name: string, sections: Array<{ sectionId: number; questions: Array<{ mappingId: number; order: number }> }>): Promise<{ success: boolean; message: string; data?: { setId: number } }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/sets`, { name, sections });
    return response.data as any;
  }

  /** Update a question set */
  async updateSet(testId: number, setId: number, name: string, sections: Array<{ sectionId: number; questions: Array<{ mappingId: number; order: number }> }>): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.put(`${this.baseUrl}/${testId}/sets/${setId}`, { name, sections });
    return response.data as any;
  }

  /** Delete a question set */
  async deleteSet(testId: number, setId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/sets/${setId}`);
    return response.data as any;
  }

  /** Get section questions with tags (Java: preQuestionTagMapping) */
  async getSectionQuestions(testId: number, sectionId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/sections/${sectionId}/questions`);
    return (response.data as any)?.data;
  }

  /** Download the question tag mapping Excel template for a section (Java: generateQuestionTagMappingTemplate) */
  async downloadQuestionTagMappingTemplate(testId: number, sectionId: number): Promise<void> {
    const response = await myAxios.get(
      `${this.baseUrl}/${testId}/sections/${sectionId}/tag-mapping-template`,
      { responseType: "blob" },
    );
    const blob = new Blob([response.data as unknown as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "QuestionTagMappingTemplate.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  /** Upload a filled question tag mapping Excel for a section (Java: uploadQuestionTagMapping) */
  async uploadQuestionTagMapping(
    testId: number,
    sectionId: number,
    file: File,
  ): Promise<{ success: boolean; message: string; summary?: { rows: number; feedbackUpdated: number; answersUpdated: number; tagsAdded: number } }> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await myAxios.post(
      `${this.baseUrl}/${testId}/sections/${sectionId}/tag-mapping-import`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data as any;
  }

  /** Add tags to a question */
  async addQuestionTags(testId: number, sectionId: number, questionId: number, tagIds: string): Promise<any> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/sections/${sectionId}/questions/${questionId}/tags`, { tagIds });
    return response.data as any;
  }

  /** Get question tags grouped by type */
  async getQuestionTags(questionId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/questions/${questionId}/tags`);
    return (response.data as any)?.data;
  }

  /** Delete a question tag mapping */
  async deleteQuestionTag(qtmId: number): Promise<any> {
    const response = await myAxios.delete(`${this.baseUrl}/question-tag/${qtmId}`);
    return response.data as any;
  }

  /** Replace a question in a section */
  async replaceQuestion(testId: number, sectionId: number, mappingId: number, oldQuestionId: number, newQuestionId: number): Promise<any> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/sections/${sectionId}/replace-question`, { mappingId, oldQuestionId, newQuestionId });
    return response.data as any;
  }

  /** Delete a specific question from section */
  async deleteSpecificQuestion(testId: number, sectionId: number, mappingId: number): Promise<any> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/sections/${sectionId}/questions/${mappingId}`);
    return response.data as any;
  }

  /** Delete all questions from section */
  async deleteAllQuestions(testId: number, sectionId: number): Promise<any> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/sections/${sectionId}/questions`);
    return response.data as any;
  }

  /** Student audit trail (DynamoDB + RDS) */
  async getStudentAuditTrail(testId: number, enquiryId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/student-audit/${enquiryId}`);
    return (response.data as any)?.data;
  }

  /** Student DynamoDB responses */
  async getStudentDynamoResponses(testId: number, enquiryId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/student-dynamo/${enquiryId}`);
    return (response.data as any)?.data;
  }

  /** Webcam proctoring data (Java: getWebProctoring + WebCamProctoring.jsp) */
  async getWebProctoring(testId: number, enquiryId: number): Promise<WebProctoringData> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/web-proctoring/${enquiryId}`);
    return (response.data as any)?.data as WebProctoringData;
  }

  /** Toggle the per-student test block (block_test). */
  async setTestBlock(testId: number, enquiryId: number, blocked: boolean): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/web-proctoring/${enquiryId}/test-block`, { blocked });
    return response.data as any;
  }

  /** Set the blanket block flag ("BLOCK" | "ALLOW" | ""). */
  async setBlanketBlock(testId: number, enquiryId: number, status: "BLOCK" | "ALLOW" | ""): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/web-proctoring/${enquiryId}/blanket-block`, { status });
    return response.data as any;
  }

  /**
   * Question-wise analysis for a student (same endpoint used by ilearn /test-result/:pid/:testId).
   * Returns { testName, summary, testTableData, sections: [{ sectionName, questions }] }
   */
  async getStudentTestResult(testId: number, enquiryId: number): Promise<any> {
    const url = `${Config.nodeSiteUrl}v1/test-result/${testId}/student/${enquiryId}`;
    const response = await myAxios.get(url);
    return (response.data as any)?.data;
  }

  /** Dyna >>> RDS */
  async dyna2Rds(testId: number, enquiryId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/student-dynamo/${enquiryId}/dyna2rds`);
    return response.data as any;
  }

  /** Delete DynamoDB responses */
  async deleteStudentDynamoResponses(testId: number, enquiryId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/student-dynamo/${enquiryId}`);
    return response.data as any;
  }

  /** Populate student data (RDS recalculate from stsqm) */
  async populateStudentData(testId: number, enquiryId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/student-populate/${enquiryId}`);
    return response.data as any;
  }

  /** Delete student test responses (DynamoDB) */
  async deleteStudentResponses(testId: number, enquiryId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/student-dynamo/${enquiryId}`);
    return response.data as any;
  }

  /** Delete student RDS marks (Java: deleteStudentTestMarks) */
  async deleteStudentMarks(testId: number, enquiryId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/student-marks/${enquiryId}`);
    return response.data as any;
  }

  /** Export results to Excel (download .xlsx) */
  getResultsExcelUrl(testId: number): string {
    return `${this.baseUrl}/${testId}/results/export-excel`;
  }

  /** Upload results Excel to S3 cloud */
  async uploadResultsToCloud(testId: number): Promise<{ success: boolean; message: string; data?: { s3Key: string } }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/results/upload-cloud`);
    return response.data as any;
  }

  /** Get today's test statistics */
  async getTodayTestStatistics(date?: string): Promise<any> {
    const params = date ? `?date=${date}` : "";
    const response = await myAxios.get(`${this.baseUrl}/today-statistics${params}`);
    return (response.data as any)?.data;
  }

  /** Get today's activity roll (proctoring images) */
  async getTodayActivityRoll(date?: string): Promise<any> {
    const params = date ? `?date=${date}` : "";
    const response = await myAxios.get(`${this.baseUrl}/today-activity${params}`);
    return (response.data as any)?.data;
  }

  /** Get today's test monitoring */
  async getTodayTestMonitoring(date?: string): Promise<any> {
    const params = date ? `?date=${date}` : "";
    const response = await myAxios.get(`${this.baseUrl}/today-monitoring${params}`);
    return (response.data as any)?.data;
  }

  /** Get tags by type */
  async getTagsByType(typeId: number): Promise<{ tags: Array<{ id: number; name: string }> }> {
    const response = await myAxios.get(`${this.baseUrl}/tag-type/${typeId}/tags`);
    return (response.data as any)?.data;
  }

  /** Get child tags for cascading dropdown */
  async getChildTags(tagId: number): Promise<{ tags: Array<{ id: number; name: string; typeId: number }> }> {
    const response = await myAxios.get(`${this.baseUrl}/tag/${tagId}/children`);
    return (response.data as any)?.data;
  }

  /** Get question-applicable tag types with root tags */
  async getQuestionTagTypes(): Promise<{ tagTypes: Array<{ id: number; classId: number; name: string; displayName: string; typeId: number; tags: Array<{ id: number; name: string }> }> }> {
    const response = await myAxios.get(`${this.baseUrl}/question-tag-types`);
    return (response.data as any)?.data;
  }

  /** Add questions to section */
  async addQuestionsToSection(testId: number, sectionId: number, questionIds: string, marks: number, negativeMarks: number = 0): Promise<{ success: boolean; message: string; added?: number; skipped?: number }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/sections/${sectionId}/add-questions`, { questionIds, marks, negativeMarks });
    return response.data as any;
  }

  async getTagLookups(testId: number): Promise<{
    tagTypes: Array<{ id: number; name: string; code: string }>;
    tags: Array<{ id: number; name: string; typeId: number }>;
    mappedTags: Array<{ mappingId: number; tagId: number; tagName: string; typeId: number }>;
  }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/tag-lookups`);
    return (response.data as any)?.data;
  }

  /** Add tag mapping */
  async addTagMapping(testId: number, tagId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/tag-mapping`, { tagId });
    return response.data as any;
  }

  /** Remove tag mapping */
  async removeTagMapping(testId: number, mappingId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/tag-mapping/${mappingId}`);
    return response.data as any;
  }

  /** Get students mapped to test */
  async getTestStudents(testId: number): Promise<{ students: Array<{ mappingId: number; enquiryId: number; firstName?: string; lastName?: string; studentCode?: string }> }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/students`);
    return (response.data as any)?.data;
  }

  /** Update course publish status */
  async updateCoursePublishStatus(testId: number, mappingId: number, publishStatusId: number): Promise<{ success: boolean }> {
    const response = await myAxios.patch(`${this.baseUrl}/${testId}/issue-course/${mappingId}`, { publishStatusId });
    return response.data as any;
  }

  /** Update test-level publish status (test_master.PublishStatusId) — triggers incremental S3 re-bundle */
  async updateTestPublishStatus(testId: number, publishStatusId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.patch(`${this.baseUrl}/${testId}/publish-status`, { publishStatusId });
    return response.data as any;
  }

  /** Current public/private state of the test's solution JSON (seeds the "Make Solutions Public" toggle) */
  async getSolutionsPublicState(testId: number): Promise<{ isPublic: boolean }> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/solutions-public`);
    return (response.data as any)?.data;
  }

  /** Grant/revoke public read on the test's solution JSON files */
  async setSolutionsPublic(testId: number, access: "Public" | "Private"): Promise<{ success: boolean; isPublic: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/solutions-public`, { access });
    return response.data as any;
  }

  /** Update school mapping date */
  async updateSchoolMappingDate(testId: number, mappingId: number, testDate: string): Promise<{ success: boolean }> {
    const response = await myAxios.patch(`${this.baseUrl}/${testId}/issue-school/${mappingId}`, { testDate });
    return response.data as any;
  }

  /** Remove student from test */
  async removeStudent(testId: number, mappingId: number): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/students/${mappingId}`);
    return response.data as any;
  }

  /** Add students to test */
  async addStudents(testId: number, type: "enquiryId" | "studentCode", ids: string): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/add-students`, { type, ids });
    return response.data as any;
  }

  /** Get question rules for a test */
  async getQuestionRules(testId: number): Promise<any> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/question-rules`);
    return (response.data as any)?.data;
  }

  /** Save a question rule for a test */
  async saveQuestionRule(
    testId: number,
    rule: {
      sectionId: number;
      numberOfQuestion: number;
      marksPerQuestion: number;
      negativeMarks: number;
      questionCategories: number[];
      tags: number[];
    }
  ): Promise<any> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/question-rules`, rule);
    return response.data as any;
  }

  /** Delete a question rule */
  async deleteQuestionRule(testId: number, ruleId: number): Promise<any> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/question-rules/${ruleId}`);
    return response.data as any;
  }

  /** Make test papers based on rules */
  async makeTestPapers(testId: number, numberOfPapers: number): Promise<any> {
    const response = await myAxios.post(`${this.baseUrl}/${testId}/make-test-papers`, { numberOfPapers });
    return response.data as any;
  }

  /** Delete all generated test papers */
  async deleteTestPapers(testId: number): Promise<any> {
    const response = await myAxios.delete(`${this.baseUrl}/${testId}/question-rules-papers`);
    return response.data as any;
  }

  /**
   * List quizzes with optional filters
   */
  async listQuizzes(
    params: {
      categoryId?: number;
      testTypeId?: number;
      playerId?: number;
      /** YYYY-MM-DD — matches DATE(test_master.TestDate) on the server. */
      testDate?: string;
      /** Joined via test_year_mapping.YearId. */
      yearId?: number;
      /**
       * Centre filter against test_school_mapping. A numeric id returns tests
       * mapped to that school; the literal "unassigned" returns tests with no
       * school/tag mapping; omitted returns only tests mapped to some centre.
       */
      schoolId?: number | "unassigned";
      limit?: number;
      offset?: number;
      q?: string;
      testId?: number;
    } = {}
  ): Promise<{
    quizzes: QuizListItem[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }> {
    const response = await myAxios.get(`${this.baseUrl}/list`, { params });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data;
  }

  /**
   * Get tests mapped to a program, with optional year filter.
   * Also returns lookups (programs, years, categories) for dropdowns.
   */
  async getProgramTests(
    programId?: number,
    yearId?: number,
  ): Promise<{
    tests: ProgramTestItem[];
    lookups: ProgramTestsLookups;
    programCategories: Array<{ id: number; name: string }>;
  }> {
    const params: Record<string, number> = {};
    if (programId) params.programId = programId;
    if (yearId) params.yearId = yearId;
    const response = await myAxios.get(`${this.baseUrl}/program-tests`, { params });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response.data as any)?.data;
  }

  /**
   * Remove all test_course_mapping rows for a test within a program.
   */
  async removeProgramTest(
    testId: number,
    programId: number,
  ): Promise<{ success: boolean; message: string }> {
    const response = await myAxios.delete(`${this.baseUrl}/program-tests`, {
      data: { testId, programId },
    });
    return response.data as any;
  }

  /**
   * Generate admission tickets (hall tickets) PDF for all students mapped to a test.
   * Returns a Blob containing the PDF.
   */
  async generateHallTickets(testId: number): Promise<Blob> {
    const response = await myAxios.get(`${this.baseUrl}/${testId}/hall-tickets`, {
      responseType: "blob",
    });
    return response.data as Blob;
  }

}

export default new QuickQuizService();
